import {
  Autocomplete,
  Box,
  FormControl,
  TextField,
} from '@mui/material';
import type { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { useMemo, type SyntheticEvent } from 'react';
import {
  ATLAS_LENS_COMPONENT_VARS,
  onboardingOutlinedInputRootSx,
} from '../Form/formFieldSpec';
import { AtlasLensFormFieldError } from '../Form/AtlasLensFormFieldError';
import { OnboardingFieldLabel } from './OnboardingStepHeader';
import { OnboardingFormFieldPopper } from './OnboardingFormFieldPopper';
import { useOnboardingLens } from './onboardingLens';

interface OnboardingFormSelectOption {
  value: string;
  label: string;
}

interface OnboardingFormSelectProps {
  id: string;
  label: string;
  value: string;
  options: ReadonlyArray<OnboardingFormSelectOption>;
  onChange: SelectProps<string>['onChange'];
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Elevates the select menu above a full-viewport side sheet when set. */
  menuZIndex?: number;
}

const selectedValueRenderSx = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  width: '100%',
  minWidth: 0,
};

/** Atlas Simple dropdown — Figma form field with outlined select. */
export function OnboardingFormSelect({
  id,
  label,
  value,
  options,
  onChange,
  error,
  placeholder,
  required = false,
  disabled = false,
  menuZIndex,
}: OnboardingFormSelectProps) {
  const { layout, typography, colors, focusRing, divider } = useOnboardingLens();
  const { formField, footer } = layout;

  const selectOptions = useMemo(
    () => options.map((option) => ({ value: option.value, label: option.label })),
    [options],
  );

  const selectedOption = useMemo(
    () => selectOptions.find((option) => option.value === value),
    [selectOptions, value],
  );

  const selectRootSx = useMemo(
    () => ({
      height: formField.inputHeight,
      borderRadius: formField.inputRadius,
      ...onboardingOutlinedInputRootSx,
      '&&.Mui-focused': {
        boxShadow: focusRing,
      },
      ...(error
        ? {
            '& fieldset, & .MuiOutlinedInput-notchedOutline': {
              borderColor: ATLAS_LENS_COMPONENT_VARS.inputOutlinedBorderError,
            },
          }
        : {}),
    }),
    [error, focusRing, formField.inputHeight, formField.inputRadius],
  );

  const selectPadding = {
    pt: formField.inputPaddingY,
    pr: `calc(${formField.inputPaddingX} + ${formField.selectIconGutter})`,
    pb: formField.inputPaddingY,
    pl: formField.inputPaddingX,
  };

  const autocompleteFieldSx = useMemo(
    () => ({
      '& .MuiOutlinedInput-root': {
        width: '100%',
        maxWidth: '100%',
        padding: 0,
        ...selectRootSx,
        ...(disabled
          ? {
              backgroundColor: 'action.disabledBackground',
              '& fieldset, & .MuiOutlinedInput-notchedOutline': {
                borderColor: 'action.disabled',
              },
            }
          : {}),
      },
      '& .MuiOutlinedInput-root .MuiAutocomplete-input, & .MuiOutlinedInput-root .MuiOutlinedInput-input': {
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        width: '100%',
        minWidth: 0,
        minHeight: 'unset',
        height: formField.inputHeight,
        ...selectPadding,
        ...typography.body,
        color: disabled ? 'text.disabled' : value ? colors.typeDefault : colors.typeMuted,
        cursor: disabled ? 'default' : 'pointer',
        '&::placeholder': {
          color: disabled ? 'text.disabled' : colors.typeMuted,
          opacity: 1,
        },
      },
      '& .MuiAutocomplete-endAdornment': {
        position: 'absolute',
        top: '50%',
        right: formField.inputPaddingX,
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
      },
    }),
    [
      colors.typeDefault,
      colors.typeMuted,
      disabled,
      formField.inputHeight,
      formField.inputPaddingX,
      formField.inputPaddingY,
      formField.selectIconGutter,
      selectPadding,
      selectRootSx,
      typography.body,
      value,
    ],
  );

  const listboxSx = useMemo(
    () => ({
      '& .MuiAutocomplete-option:not(:last-of-type)': {
        borderBottom: `1px solid ${divider}`,
      },
    }),
    [divider],
  );

  const paperSx = useMemo(
    () => ({
      borderRadius: footer.menuBorderRadius,
      boxShadow: footer.menuShadow,
    }),
    [footer.menuBorderRadius, footer.menuShadow],
  );

  const handleChange = (_event: SyntheticEvent, newValue: OnboardingFormSelectOption | null) => {
    onChange?.(
      { target: { value: newValue?.value ?? '', name: id } } as SelectChangeEvent<string>,
      null,
    );
  };

  return (
    <FormControl error={Boolean(error)} fullWidth sx={{ width: '100%', minWidth: 0, overflow: 'visible' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: formField.labelToInputGap,
          width: '100%',
        }}
      >
        <OnboardingFieldLabel
          htmlFor={id}
          label={label}
          required={required}
          disabled={disabled}
        />

        <Autocomplete
          id={id}
          fullWidth
          disabled={disabled}
          disableClearable
          openOnFocus
          options={selectOptions}
          value={selectedOption}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, selected) => option.value === selected.value}
          onChange={handleChange}
          slots={{ popper: OnboardingFormFieldPopper }}
          slotProps={{
            popper:
              menuZIndex != null
                ? { style: { zIndex: menuZIndex }, sx: { zIndex: menuZIndex } }
                : undefined,
            listbox: { sx: listboxSx },
            paper: { sx: paperSx },
            popupIndicator: {
              sx: {
                width: layout.icon.md,
                height: layout.icon.md,
                color: disabled ? 'action.disabled' : colors.typeDefault,
                p: 0,
                m: 0,
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              error={Boolean(error)}
              variant="outlined"
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  readOnly: true,
                  autoComplete: 'off',
                },
              }}
              sx={autocompleteFieldSx}
            />
          )}
          renderOption={(optionProps, option) => {
            const { key, ...optionLiProps } = optionProps;
            return (
              <Box
                component="li"
                key={key}
                {...optionLiProps}
                sx={{
                  ...typography.body,
                  color: colors.typeDefault,
                  ...selectedValueRenderSx,
                }}
              >
                {option.label}
              </Box>
            );
          }}
        />
      </Box>
      {error && <AtlasLensFormFieldError message={error} />}
    </FormControl>
  );
}
